import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, EffectFade } from "swiper/modules";
import "./Bibliography.scss";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/styles.css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

const Bibliography = () => {

  const [lightboxOpenIndex, setLightboxOpenIndex] = useState(null);
  const prevElRef = useRef(null);
  const nextElRef = useRef(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current && prevElRef.current && nextElRef.current) {
      swiperRef.current.params.navigation.prevEl = prevElRef.current;
      swiperRef.current.params.navigation.nextEl = nextElRef.current;
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
    }
  }, []);


  const books = [
    {
      id: 0,
      cover: "/images/books/everything-is-the-same-but-everything-is-different/1.jpg",
      title: "Everything Is The Same But Everything Is Diffferent",
      description:
        "310×240mm | 40 pages | Hardcover with a craft rubber finished box | Bilingual (English)",
      design: "Ioannis Markakis",
      editors: "Efi Haliori, Ioannis Markakis",
      text: "Christoforos Marinos",
      img: [
        "/images/books/everything-is-the-same-but-everything-is-different/1.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/2.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/3.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/4.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/5.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/6.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/7.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/8.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/9.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/10.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/11.jpg",
        "/images/books/everything-is-the-same-but-everything-is-different/12.jpg",
      ],
      summary: "<p>The sea is the same everywhere, but always different. It changes, each time, depending on where and on how we look at it, the moment in time and our emotional state. The sea, as a subject, is as commonplace as it is quintessentially classic. To be precise, it bears all the properties of the classic: simplicity, balance, perfection, harmony, timelessness. It is a primeval image; its value transcends the boundaries of historical time and social space. The sea, however, is ruled by unpredictable forces, and the shipwreck is a possibility facing every traveller, as the philosopher Hans Blumenberg reminds us in his essay Shipwreck with Spectator. The sea begets awe. It is no accident that the entire Bible is characterised by fear of the sea and aversion towards it. On the other hand, the magnetic attraction it exerts upon poets and artists is widely known. Gallons of ink have been shed for the sea. Countless paintings have been made and innumerable photographs are taken every day, by professionals and amateurs alike. What novelty, then, could yet another series of works on the sea have to offer?</p><p>Efi Haliori’s photographs verify the singularity of the gaze. It may seem ordinary at first glance, but the view of the sea she captures with her lens is distinctive, exclusively her own. It is an emotionally charged view, because it is connected to the contemporary Odyssey of thousands of refugees trying to cross – not always successfully – the South Aegean from Turkey. Taken in the spring of 2015 in Chios and Lesvos, Haliori’s photographs have an implicit function – in contrast to photojournalism, they do not exploit the drama of refugeedom. Her images hint at everything that takes place in these turbulent waters: the short but supremely dangerous journey on the overloaded boats, the moments of anxiety, the pervasive fear, the gestures of despair, the moments of panic and desolation, the mass drownings, the bodies washed up on the beautiful beaches, the sobs and the tears for drowned children, the fishermen finding dead refugees in their nets instead of writhing fish.</p><p>That sea – the Mediterranean – has become a monument to the drowned refugees. The scenery will never be the same again. The natural environment may remain unchanged through time, but collective memory deems it dramatic. Everything has changed, even if we cannot see it. From Skala Sykamineas in Lesvos, a small fishing village in the north of the island, Haliori turns her camera towards the coast of Turkey and photographs a dark, dreary sea. The sky is expressionless, sometimes ashen, sometimes pure white. In certain photographs, the sea, the land, and the sky take on a single colour that is reminiscent of anything but spring. The sea shows its many faces: at times it’s frothy, with gentle ripples, at other times it's black as night, and mournful to match. In some photographs, it seems to have turned to stone, its crinkled surface chiselled by the light. In others yet, it appears hazy, creating the impression that it’s been painted. It is a cold and otherworldly sea. A sea in transition. It changes from day to day, from one moment to the next: it may equally be peaceful as it may be rough. The night-time shots from Chios, under the light of the moon or the dim glow of the night, demonstrate its diversity and its mysterious charm to perfection. </p><p>Haliori doesn’t need to travel across the world – like Hiroshi Sugimoto – to conceive of the endlessness of the sea and capture its power. Nor does she have either the intention or the need to beautify the Greek beaches by photographing their sparkling blue waters. On the contrary, she adopts a stark, utilitarian style (undeniably melancholic), which emphasises the dark side of the sea. Haliori strips the landscape of the romantic and tourist gaze, to convey the visual information without cliched sentimentalities, without enchanting sunsets and sunrises. Her images are moving for their unpretentious simplicity and sober approach. And most importantly: they remind you how danger and tragedy often lurk in the beautiful and the wonderful.</p><p class=\"font-size-12 text-end\">Christoforos Marinos</p>"
    }
  ];

  const [openLightbox, setOpenLightbox] = useState(
    Array(books.length).fill(false)
  );

  return (
    <section id="bibliography">
      <div className="container container-medium bibliography-container">
        <h1 className="mb-4">Bibliography</h1>
        <div className="accordion" id="bibliographyAccordion">
          {books.map((book, index) => {
            return (
              <div className="accordion-item position-relative" key={index}>
                <div className="row pb-3 pb-lg-4">
                  <div className="col-md-5">
                    <div className="position-relative">
                      <div className="d-none">{book.title}</div>

                      <Swiper
                        id={`book-swiper-${index}`}
                        className="book-swiper pb-lg-5"
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                        navigation={{
                          prevEl: prevElRef.current,
                          nextEl: nextElRef.current,
                          disabledClass: "swiper-button-disabled",
                        }}
                        breakpoints={{
                          10: {
                            autoHeight: false,
                          },
                          992: {
                            autoHeight: false,
                          },
                        }}
                        modules={[Navigation, EffectFade]}
                        slidesPerView={1}
                        spaceBetween={10}
                        effect="fade"
                        fadeEffect={{
                          crossFade: true,
                        }}
                      >
                        {book.img.map((image, idx) => (
                          <SwiperSlide key={image}>
                            <div className="project-img-wrapper">
                              <img src={image} className="img-fluid" alt={`Artwork ${idx + 1}`} />
                              <button
                                type="button"
                                className="full-box-link lightbox-open bg-transparent border-0"
                                aria-label={`Open lightbox for slide ${idx + 1}`}
                                onClick={() => setLightboxOpenIndex(idx)}
                              />
                            </div>
                          </SwiperSlide>
                        ))}

                        <div className="slider-controls project-swiper-navigation swiper-navigation">
                          <div ref={prevElRef} className="swiper-button-prev" />
                          <div ref={nextElRef} className="swiper-button-next" />
                        </div>
                      </Swiper>

                      <Lightbox
                        open={lightboxOpenIndex !== null}
                        close={() => setLightboxOpenIndex(null)}
                        slides={book.img.map((src) => ({ src }))}
                        index={lightboxOpenIndex || 0}
                        carousel={{ finite: true }}
                      />
                    </div>
                  </div>
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100">
                      <div>
                        <span className="d-block text-uppercase fw-bold font-size-14 me-4 pe-2 pt-2 pb-1 py-lg-0">
                          {book.title}
                        </span>
                        <p className="font-size-12 mb-1">{book.description}</p>
                        <p className="font-size-12 mb-1">Publication Editors: {book.editors}</p>
                        <p className="font-size-12 mb-1">Design: {book.design}</p>
                        <p className="font-size-12 mb-1">Texts: {book.text}</p>
                      </div>
                      {/* <div className="mt-auto">
                        <button className="accordion-button collapsed font-semibold font-size-13 w-auto lh-1 mt-2 mt-lg-0" type="button" data-bs-toggle="collapse" data-bs-target={"#collapse" + index} aria-expanded="false"
                          aria-controls={"collapse" + index}>
                          <span className="see-more-text d-inline-block">
                            See More
                          </span>
                          <span className="see-less-text d-none">
                            See Less
                          </span>
                        </button>
                      </div> */}
                    </div>
                  </div>

                  <Lightbox
                    open={openLightbox[index]}
                    close={() => {
                      const updatedOpenLightbox = [...openLightbox];
                      updatedOpenLightbox[index] = false;
                      setOpenLightbox(updatedOpenLightbox);
                    }}
                    slides={book.img.map((src) => ({ src }))}
                    carousel={{ finite: true }}
                  />

                </div>

                {/* <div id={"collapse" + index} className="accordion-collapse collapse multi-collapse"
                  aria-labelledby={"collapse" + index}>
                  <div className="font-size-14" dangerouslySetInnerHTML={{ __html: book.summary }} />
                </div> */}
              </div>
            );
          })}
        </div>
      </div>
    </section >
  );
};

export default Bibliography;